import type { CSSProperties, ReactNode } from 'react';
import styles from './ChartTooltip.module.css';

export interface ChartTooltipRow {
  label: string;
  value: ReactNode;
  /** Series colour — a token or a resolved colour. */
  color: string;
}

export interface ChartTooltipTotal {
  label: string;
  value: ReactNode;
  /** Colour the value: crimson (primary) or gold (secondary) for the line the chart is about. */
  emphasis?: 'primary' | 'secondary';
}

export interface ChartTooltipProps {
  /** The hovered quarter, e.g. "Q2 2026". Omit when the host already titles it. */
  title?: ReactNode;
  rows?: ChartTooltipRow[];
  /** Ruled-off summary lines under the rows (total, churn rate…). */
  totals?: ChartTooltipTotal[];
  /** 'bare' drops the card chrome, for content inside another tooltip bubble. */
  variant?: 'card' | 'bare';
}

/**
 * Hover card for the profile charts. Recharts calls a `content` render prop;
 * each chart maps its payload to rows and returns this.
 */
export function ChartTooltip({ title, rows = [], totals = [], variant = 'card' }: ChartTooltipProps) {
  return (
    <div className={variant === 'card' ? styles.tooltip : styles.bare}>
      {title != null && <div className={styles.title}>{title}</div>}
      {rows.map((row) => (
        <div key={row.label} className={styles.row}>
          <span className={styles.dot} style={{ '--color': row.color } as CSSProperties} />
          <span className={styles.name}>{row.label}</span>
          <span className={styles.value}>{row.value}</span>
        </div>
      ))}
      {totals.map((t) => (
        <div key={t.label} className={styles.total}>
          <span>{t.label}</span>
          <span className={t.emphasis && styles[t.emphasis]}>{t.value}</span>
        </div>
      ))}
    </div>
  );
}
